import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Chrome, Shield, ArrowRight, GraduationCap, Lightbulb, Lock, Monitor, Smartphone, Wifi, CheckCircle2, HardDrive, Bot, Briefcase, Target, Cpu, Zap, Apple, Settings, Terminal, Newspaper, MessageSquare, Edit, AlertTriangle, Fingerprint, Eye, Key, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { mockCategories } from '../mock';
import Footer from './Footer';

const HomePage = ({ searchResults = [], searchQuery = '', onSearch }) => {
  const navigate = useNavigate();
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Suggestions d'autocomplétion basées sur le contenu
  const searchSuggestions = [
    // Office 365
    'Office 365', 'Outlook', 'Word', 'Excel', 'PowerPoint', 'Teams', 'OneDrive', 'SharePoint', 'Planner', 'Copilot',
    // Google Workspace  
    'Google Workspace', 'Gmail', 'Google Docs', 'Google Sheets', 'Google Slides', 'Google Meet', 'Google Drive', 'Google Calendar',
    // Sécurité
    'Sécurité', 'Mots de passe', 'Phishing', '2FA', 'Authentification', 'WiFi sécurisé', 'VPN', 'Antivirus',
    // Mobile
    'iPhone', 'iPad', 'Android', 'iOS', 'Configuration mobile', 'Apps', 'Synchronisation',
    // IA
    'Intelligence Artificielle', 'ChatGPT', 'Claude', 'IA générative', 'Copilot', 'Automatisation',
    // Synology
    'Synology', 'NAS', 'Drive', 'DSM', 'Sauvegarde', 'Stockage',
    // OS
    'Windows', 'macOS', 'Linux', 'Système exploitation'
  ];

  // Filtrer les suggestions basées sur la saisie
  const filteredSuggestions = searchSuggestions.filter(suggestion =>
    suggestion.toLowerCase().includes(localSearchQuery.toLowerCase()) && 
    localSearchQuery.trim().length > 0
  ).slice(0, 6); // Limiter à 6 suggestions

  // Helper function to format date in European format
  const formatDateEuropean = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Handle search function
  const handleSearch = (e) => {
    e.preventDefault();
    if (localSearchQuery.trim()) {
      // Navigate to search results page with query
      navigate(`/search?q=${encodeURIComponent(localSearchQuery)}`);
    }
  };
  // Show search results if there's an active search
  if (searchQuery) {
    if (searchResults.length > 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
          <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Résultats de recherche pour "{searchQuery}"
                </h2>
                <p className="text-lg text-gray-600">
                  {searchResults.length} guide{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.map((guide) => (
                  <Card key={guide.id} className="cursor-pointer hover:shadow-lg transition-shadow duration-300">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          guide.difficulty === 'Débutant' ? 'bg-green-100 text-green-800' :
                          guide.difficulty === 'Intermédiaire' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {guide.difficulty}
                        </span>
                        <span className="text-sm text-gray-500">{guide.estimatedTime}</span>
                      </div>
                      <CardTitle className="text-xl">{guide.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-600 mb-4">
                        {guide.description}
                      </CardDescription>
                      <div className="flex flex-wrap gap-1 mb-4">
                        {guide.tags.map(tag => (
                          <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <Button 
                        onClick={() => {
                          // Navigate to the appropriate category for this guide
                          const categories = ['office365', 'google-workspace', 'security', 'mobile'];
                          for (const cat of categories) {
                            // Navigate to the first category with results
                            navigate(`/${cat}`);
                            break;
                          }
                        }}
                        className="w-full"
                      >
                        Voir le guide
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        </div>
      );
    } else {
      // No search results found
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
          <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-6xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Aucun résultat pour "{searchQuery}"
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Essayez avec d'autres termes de recherche ou explorez nos catégories ci-dessous.
              </p>
              <Button 
                onClick={() => window.location.reload()}
                className="mb-8"
              >
                Retour à l'accueil
              </Button>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                {mockCategories.map((category) => {
                  const getIcon = (iconName) => {
                    switch(iconName) {
                      case 'Building2': return Building2;
                      case 'Chrome': return Chrome;
                      case 'Shield': return Shield;
                      case 'Smartphone': return Smartphone;
                      case 'HardDrive': return HardDrive;
                      case 'Bot': return Bot;
                      default: return GraduationCap;
                    }
                  };
                  const IconComponent = getIcon(category.icon);
                  
                  return (
                    <Link to={`/${category.id}`} key={category.id} className="block">
                      <Card 
                        className="group cursor-pointer transition-all duration-300 hover:shadow-xl border border-gray-200 hover:border-gray-300 bg-white h-full"
                      >
                      <CardHeader className="text-center pb-4">
                        <div className="mx-auto w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mb-4 group-hover:bg-gray-100 transition-colors duration-200">
                          <IconComponent className={`h-8 w-8 ${category.textColor}`} />
                        </div>
                        <CardTitle className="text-xl font-bold text-gray-900">
                          {category.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-center pt-0">
                        <CardDescription className="text-gray-600 mb-6 leading-relaxed">
                          {category.description}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </Link>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-20 leading-relaxed">
              <span className="block">Guides pratiques</span>
              <span className="block bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                professionnels
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-indigo-100 max-w-3xl mx-auto mb-12">
              Un ensemble de guides, de procédures et de liens pour vous accompagner au quotidien
            </p>
            
            {/* Search Bar integrated in hero */}
            <div className="max-w-4xl mx-auto relative">
              <form onSubmit={handleSearch} className="relative">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-indigo-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Recherchez parmi tous nos guides et actualités..."
                  className="w-full pl-14 pr-36 py-5 text-base bg-white bg-opacity-95 backdrop-blur-md border border-white border-opacity-30 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 focus:bg-opacity-100 transition-all duration-300 shadow-2xl"
                  value={localSearchQuery}
                  onChange={(e) => {
                    setLocalSearchQuery(e.target.value);
                    setShowSuggestions(e.target.value.trim().length > 0);
                  }}
                  onFocus={() => setShowSuggestions(localSearchQuery.trim().length > 0)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                />
                <Button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-400 hover:to-orange-500 text-white font-bold px-6 py-3 rounded-md hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Rechercher
                </Button>
                
                {/* Autocomplete Suggestions */}
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 overflow-hidden backdrop-blur-lg">
                    {filteredSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setLocalSearchQuery(suggestion);
                          setShowSuggestions(false);
                          navigate(`/search?q=${encodeURIComponent(suggestion)}`);
                        }}
                        className="w-full text-left px-5 py-3 hover:bg-indigo-50 transition-colors duration-200 text-gray-700 border-b border-gray-100 last:border-b-0 flex items-center group"
                      >
                        <Search className="h-4 w-4 mr-3 text-indigo-400 group-hover:text-indigo-600 transition-colors" />
                        <span className="font-medium group-hover:text-indigo-800 transition-colors">{suggestion}</span>
                      </button>
                    ))}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-pink-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-cyan-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      {/* Categories Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl mb-8 animate-pulse shadow-2xl">
              <Briefcase className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-6">
              Les outils de production
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Maîtrisez les suites bureautiques essentielles • Optimisez votre productivité • Gérez vos infrastructures
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Office 365 */}
            <Link to="/office365" className="block h-full">
              <Card className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden relative flex flex-col h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 opacity-5 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"></div>
                
                <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                  <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Building2 className="h-10 w-10 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    Office 365
                  </CardTitle>
                  <CardDescription className="text-gray-600 mb-6 leading-relaxed text-lg">
                    Guides complets pour maîtriser la suite Office 365
                  </CardDescription>
                </CardHeader>
                
                {/* Bottom Action Bar */}
                <div className="bg-gray-900 group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-indigo-600 transition-all duration-300 p-4 relative z-10">
                  <Button variant="ghost" className="w-full text-white font-bold text-lg hover:bg-transparent">
                    Consulter le guide
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </Button>
                </div>
                
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
              </Card>
            </Link>

            {/* Google Workspace */}
            <Link to="/google-workspace" className="block h-full">
              <Card className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden relative flex flex-col h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-600 opacity-5 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"></div>
                
                <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                  <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Chrome className="h-10 w-10 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    Google Workspace
                  </CardTitle>
                  <CardDescription className="text-gray-600 mb-6 leading-relaxed text-lg">
                    Bonnes pratiques pour Google Workspace et ses outils
                  </CardDescription>
                </CardHeader>
                
                {/* Bottom Action Bar */}
                <div className="bg-gray-900 group-hover:bg-gradient-to-r group-hover:from-green-500 group-hover:to-emerald-600 transition-all duration-300 p-4 relative z-10">
                  <Button variant="ghost" className="w-full text-white font-bold text-lg hover:bg-transparent">
                    Consulter le guide
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </Button>
                </div>
                
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
              </Card>
            </Link>

            {/* Synology */}
            <Link to="/synology" className="block h-full">
              <Card className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden relative flex flex-col h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-cyan-600 opacity-5 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"></div>
                
                <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                  <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <HardDrive className="h-10 w-10 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    Synology
                  </CardTitle>
                  <CardDescription className="text-gray-600 mb-6 leading-relaxed text-lg">
                    Maîtrisez votre NAS Synology : Drive, partage de fichiers et fonctionnalités essentielles
                  </CardDescription>
                </CardHeader>
                
                {/* Bottom Action Bar */}
                <div className="bg-gray-900 group-hover:bg-gradient-to-r group-hover:from-teal-500 group-hover:to-cyan-600 transition-all duration-300 p-4 relative z-10">
                  <Button variant="ghost" className="w-full text-white font-bold text-lg hover:bg-transparent">
                    Consulter le guide
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </Button>
                </div>
                
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Mobile Section - Same style as IA and Security */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-900 via-pink-900 to-rose-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-400 to-pink-500 rounded-3xl mb-8 animate-pulse shadow-2xl">
              <Smartphone className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-5xl font-bold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent mb-6">
              Appareils Mobiles
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto mb-12">
              Optimisez votre productivité mobile • iPhone et iPad • Android • Sécurité et applications
            </p>
          </div>
            
          {/* Mobile Platforms Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                id: 'apple',
                name: 'Écosystème Apple',
                icon: Apple,
                description: 'iPhone, iPad, Mac et synchronisation iCloud',
                color: 'from-gray-500 to-gray-600',
                guideUrl: '/mobile/apple',
                hasGuide: true
              },
              {
                id: 'android',
                name: 'Écosystème Android',
                icon: Bot,
                description: 'Smartphones, tablettes et personnalisation Android',
                color: 'from-green-500 to-green-600',
                guideUrl: '/mobile/android',
                hasGuide: true
              }
            ].map((platform, index) => {
              const IconComponent = platform.icon;
              
              return (
                <Link to={platform.guideUrl} key={index} className="block">
                  <Card className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-4 border-0 overflow-hidden relative bg-gradient-to-br from-purple-800 to-pink-800 backdrop-blur-sm h-full flex flex-col">
                    {/* Animated background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${platform.color} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                    
                    {/* Mobile particles effect */}
                    <div className="absolute top-4 left-4 w-2 h-2 bg-purple-300 rounded-full opacity-60 animate-ping"></div>
                    <div className="absolute top-8 right-12 w-1 h-1 bg-pink-300 rounded-full opacity-80 animate-pulse"></div>
                    <div className="absolute bottom-6 left-8 w-1.5 h-1.5 bg-rose-300 rounded-full opacity-70 animate-bounce"></div>
                    
                    <CardHeader className="text-center pb-6 relative z-10 p-8 flex-grow">
                      <div className={`mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br ${platform.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-xl`}>
                        <IconComponent className="h-10 w-10 text-white" />
                      </div>
                      <CardTitle className="text-xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors duration-300">
                        {platform.name}
                      </CardTitle>
                      <CardDescription className="text-purple-200 text-sm leading-relaxed mb-6">
                        {platform.description}
                      </CardDescription>
                    </CardHeader>
                    
                    {/* Bottom Action Bar */}
                    <div className={`bg-gray-900 group-hover:bg-gradient-to-r group-hover:${platform.color} transition-all duration-300 p-4 relative z-10`}>
                      <Button variant="ghost" className="w-full text-white font-bold text-lg hover:bg-transparent">
                        {platform.hasGuide ? 'Voir le guide' : 'Bientôt disponible'}
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                      </Button>
                    </div>
                    
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Intelligence Artificielle Section - Dedicated with IA Types */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-3xl mb-8 animate-pulse shadow-2xl">
              <Bot className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-5xl font-bold bg-gradient-to-r from-cyan-300 to-pink-300 bg-clip-text text-transparent mb-6">
              Intelligence Artificielle
            </h2>
            <p className="text-xl text-indigo-200 max-w-3xl mx-auto mb-12">
              Chaque IA à sa spécialité, trouvez l'IA qui répond à vos besoins
            </p>
          </div>
            
          {/* IA Types Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                id: 'conversational',
                name: 'IA Conversationnelles',
                icon: MessageSquare,
                description: 'ChatGPT, Claude, Gemini et assistants chat',
                color: 'from-blue-500 to-indigo-600'
              },
              {
                id: 'creative',
                name: 'IA Créatives',
                icon: Edit,
                description: 'Génération d\'images et contenu artistique',
                color: 'from-pink-500 to-purple-600'
              },
              {
                id: 'specialized',
                name: 'IA Spécialisées',
                icon: Target,
                description: 'Outils experts par secteur d\'activité',
                color: 'from-green-500 to-teal-600'
              },
              {
                id: 'productivity',
                name: 'IA Productivité',
                icon: Zap,
                description: 'Automatisation et optimisation du travail',
                color: 'from-orange-500 to-red-600'
              },
              {
                id: 'research',
                name: 'IA Recherche',
                icon: Search,
                description: 'Moteurs de recherche et analyse intelligente',
                color: 'from-purple-500 to-indigo-600'
              },
              {
                id: 'coding',
                name: 'IA Programmation',
                icon: Cpu,
                description: 'Assistance au développement et au code',
                color: 'from-cyan-500 to-blue-600'
              }
            ].map((iaType, index) => {
              const IconComponent = iaType.icon;
              
              return (
                <Link to={`/ia/${iaType.id}`} key={index} className="block">
                  <Card className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-4 border-0 overflow-hidden relative bg-gradient-to-br from-indigo-800 to-purple-800 backdrop-blur-sm h-full flex flex-col">
                    {/* Animated background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${iaType.color} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                    
                    {/* Floating particles effect */}
                    <div className="absolute top-4 left-4 w-2 h-2 bg-cyan-300 rounded-full opacity-60 animate-ping"></div>
                    <div className="absolute top-8 right-12 w-1 h-1 bg-pink-300 rounded-full opacity-80 animate-pulse"></div>
                    <div className="absolute bottom-6 left-8 w-1.5 h-1.5 bg-blue-300 rounded-full opacity-70 animate-bounce"></div>
                    
                    <CardHeader className="text-center pb-6 relative z-10 p-8 flex-grow">
                      <div className={`mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br ${iaType.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-xl`}>
                        <IconComponent className="h-10 w-10 text-white" />
                      </div>
                      <CardTitle className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors duration-300">
                        {iaType.name}
                      </CardTitle>
                      <CardDescription className="text-indigo-200 text-sm leading-relaxed mb-6">
                        {iaType.description}
                      </CardDescription>
                    </CardHeader>
                    
                    {/* Bottom Action Bar */}
                    <div className={`bg-gray-900 group-hover:bg-gradient-to-r group-hover:${iaType.color} transition-all duration-300 p-4 relative z-10`}>
                      <Button variant="ghost" className="w-full text-white font-bold text-lg hover:bg-transparent">
                        Découvrir
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                      </Button>
                    </div>
                    
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Systèmes d'exploitation Section - Dedicated */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-gray-900 to-blue-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-slate-400 to-blue-500 rounded-3xl mb-8 animate-pulse shadow-2xl">
              <Monitor className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-5xl font-bold bg-gradient-to-r from-slate-300 to-blue-300 bg-clip-text text-transparent mb-6">
              Systèmes d'exploitation
            </h2>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto mb-12">
              Maîtrisez votre environnement • Windows • macOS • Linux • Configuration et optimisation
            </p>
          </div>
            
          {/* OS Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                id: 'windows',
                name: 'Windows 11',
                icon: Monitor,
                description: 'Configuration et optimisation Windows',
                color: 'from-blue-500 to-blue-600',
                hasGuide: true,
                guideUrl: '/operating-systems/guides/windows-11-optimization'
              },
              {
                id: 'macos',
                name: 'macOS',
                icon: Apple,
                description: 'Productivité et sécurité sur Mac',
                color: 'from-gray-500 to-gray-600',
                hasGuide: true,
                guideUrl: '/operating-systems/guides/macos-productivity-security'
              },
              {
                id: 'linux',
                name: 'Linux',
                icon: Terminal,
                description: 'Administration et ligne de commande',
                color: 'from-green-500 to-green-600',
                hasGuide: true,
                guideUrl: '/operating-systems/guides/linux-administration-productivity'
              }
            ].map((os, index) => {
              const IconComponent = os.icon;
              
              return (
                <Link to={os.guideUrl} key={index} className="block">
                  <Card className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-4 border-0 overflow-hidden relative bg-gradient-to-br from-slate-800 to-gray-800 backdrop-blur-sm h-full flex flex-col">
                    {/* Animated background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${os.color} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                    
                    {/* OS particles effect */}
                    <div className="absolute top-4 left-4 w-2 h-2 bg-blue-300 rounded-full opacity-60 animate-ping"></div>
                    <div className="absolute top-8 right-12 w-1 h-1 bg-slate-300 rounded-full opacity-80 animate-pulse"></div>
                    <div className="absolute bottom-6 left-8 w-1.5 h-1.5 bg-gray-300 rounded-full opacity-70 animate-bounce"></div>
                    
                    <CardHeader className="text-center pb-6 relative z-10 p-8 flex-grow">
                      <div className={`mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br ${os.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-xl`}>
                        <IconComponent className="h-10 w-10 text-white" />
                      </div>
                      <CardTitle className="text-xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors duration-300">
                        {os.name}
                      </CardTitle>
                      <CardDescription className="text-slate-200 text-sm leading-relaxed mb-6">
                        {os.description}
                      </CardDescription>
                    </CardHeader>
                    
                    {/* Bottom Action Bar */}
                    <div className={`bg-gray-900 group-hover:bg-gradient-to-r group-hover:${os.color} transition-all duration-300 p-4 relative z-10`}>
                      <Button variant="ghost" className="w-full text-white font-bold text-lg hover:bg-transparent">
                        {os.hasGuide ? 'Voir le guide' : 'Bientôt disponible'}
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                      </Button>
                    </div>
                    
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sécurité Section - Dedicated */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-900 via-orange-900 to-yellow-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-red-400 to-orange-500 rounded-3xl mb-8 animate-pulse shadow-2xl">
              <Shield className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-5xl font-bold bg-gradient-to-r from-red-300 to-yellow-300 bg-clip-text text-transparent mb-6">
              Sécurité & Protection
            </h2>
            <p className="text-xl text-red-200 max-w-3xl mx-auto mb-12">
              Protégez vos données • Mots de passe • Anti-phishing • WiFi sécurisé • 2FA
            </p>
          </div>
            
          {/* Security Domains Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                id: 'passwords',
                name: 'Mots de passe',
                icon: Lock,
                description: 'Sécurisation et gestion des identifiants',
                color: 'from-red-500 to-red-600',
                guideUrl: '/security/guides/password-security',
                hasGuide: true
              },
              {
                id: 'phishing',
                name: 'Anti-phishing',
                icon: AlertTriangle,
                description: 'Protection contre les tentatives d\'hameçonnage',
                color: 'from-orange-500 to-red-600',
                guideUrl: '/security/guides/phishing-protection',
                hasGuide: true
              },
              {
                id: 'wifi',
                name: 'Sécurité WiFi',
                icon: Wifi,
                description: 'Connexions réseau sécurisées',
                color: 'from-blue-500 to-blue-600',
                guideUrl: '/security/guides/wifi-security',
                hasGuide: true
              },
              {
                id: 'twofa',
                name: 'Authentification 2FA',
                icon: Fingerprint,
                description: 'Double authentification et sécurité renforcée',
                color: 'from-purple-500 to-purple-600',
                guideUrl: '/security/guides/2fa-authentication',
                hasGuide: true
              },
              {
                id: 'privacy',
                name: 'Vie privée',
                icon: Eye,
                description: 'Protection des données personnelles',
                color: 'from-green-500 to-green-600',
                guideUrl: '/security',
                hasGuide: false
              },
              {
                id: 'backup',
                name: 'Sauvegarde',
                icon: Key,
                description: 'Protection et récupération des données',
                color: 'from-teal-500 to-teal-600',
                guideUrl: '/security',
                hasGuide: false
              }
            ].map((domain, index) => {
              const IconComponent = domain.icon;
              
              return (
                <Link to={domain.guideUrl} key={index} className="block">
                  <Card className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-4 border-0 overflow-hidden relative bg-gradient-to-br from-red-800 to-orange-800 backdrop-blur-sm h-full flex flex-col">
                    {/* Animated background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${domain.color} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                    
                    {/* Security particles effect */}
                    <div className="absolute top-4 left-4 w-2 h-2 bg-red-300 rounded-full opacity-60 animate-ping"></div>
                    <div className="absolute top-8 right-12 w-1 h-1 bg-orange-300 rounded-full opacity-80 animate-pulse"></div>
                    <div className="absolute bottom-6 left-8 w-1.5 h-1.5 bg-yellow-300 rounded-full opacity-70 animate-bounce"></div>
                    
                    <CardHeader className="text-center pb-6 relative z-10 p-6 flex-grow">
                      <div className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${domain.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500 shadow-xl`}>
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-lg font-bold text-white mb-2 group-hover:text-red-300 transition-colors duration-300">
                        {domain.name}
                      </CardTitle>
                      <CardDescription className="text-red-200 text-sm leading-relaxed mb-4">
                        {domain.description}
                      </CardDescription>
                    </CardHeader>
                    
                    {/* Bottom Action Bar */}
                    <div className={`bg-gray-900 group-hover:bg-gradient-to-r group-hover:${domain.color} transition-all duration-300 p-3 relative z-10`}>
                      <Button variant="ghost" className="w-full text-white font-bold text-sm hover:bg-transparent">
                        {domain.hasGuide ? 'Voir le guide' : 'Bientôt disponible'}
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                      </Button>
                    </div>
                    
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Best Practices Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl mb-6 animate-pulse">
              <Lightbulb className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
              Réflexes Numériques
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Les essentiels du digital • Sécurité • Performance • Simplicité
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Sécurité des mots de passe */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-red-200 transform hover:-translate-y-1">
              <div className="flex items-center mb-6">
                <div className="p-4 bg-gradient-to-r from-red-500 to-pink-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Lock className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <Lock className="h-5 w-5 mr-2" />
                    Mots de passe
                  </h3>
                  <p className="text-red-600 font-medium">Sécurité renforcée</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">12+ caractères minimum</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Unique par compte</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">2FA activé partout</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Gestionnaire de mots de passe</span>
                </li>
              </ul>
            </div>

            {/* Protection contre le phishing */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-orange-200 transform hover:-translate-y-1">
              <div className="flex items-center mb-6">
                <div className="p-4 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <Target className="h-5 w-5 mr-2" />
                    Anti-phishing
                  </h3>
                  <p className="text-orange-600 font-medium">Vigilance active</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Vérifier l'expéditeur</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Survoler sans cliquer</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Méfiance des urgences</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Signaler les tentatives</span>
                </li>
              </ul>
            </div>

            {/* Sécurité WiFi */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-blue-200 transform hover:-translate-y-1">
              <div className="flex items-center mb-6">
                <div className="p-4 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Wifi className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <Wifi className="h-5 w-5 mr-2" />
                    Connexions
                  </h3>
                  <p className="text-blue-600 font-medium">Sécurité réseau</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Éviter les WiFi ouverts</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">VPN sur réseaux publics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Privilégier 4G/5G</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Désactiver le partage</span>
                </li>
              </ul>
            </div>

            {/* Hygiène numérique */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-purple-200 transform hover:-translate-y-1">
              <div className="flex items-center mb-6">
                <div className="p-4 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Monitor className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <Monitor className="h-5 w-5 mr-2" />
                    Hygiène digital
                  </h3>
                  <p className="text-purple-600 font-medium">Maintenance régulière</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Logiciels à jour</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Sauvegardes régulières</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Nettoyage espace digital</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Surveillance comptes</span>
                </li>
              </ul>
            </div>

            {/* Productivité */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-green-200 transform hover:-translate-y-1">
              <div className="flex items-center mb-6">
                <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <Zap className="h-5 w-5 mr-2" />
                    Productivité
                  </h3>
                  <p className="text-green-600 font-medium">Office optimisé</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Emails organisés</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Raccourcis clavier</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Calendriers synchronisés</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Collaboration temps réel</span>
                </li>
              </ul>
            </div>

            {/* Mobilité */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-indigo-200 transform hover:-translate-y-1">
              <div className="flex items-center mb-6">
                <div className="p-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Smartphone className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <Smartphone className="h-5 w-5 mr-2" />
                    Mobile Smart
                  </h3>
                  <p className="text-indigo-600 font-medium">Sécurité nomade</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Verrouillage actif</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Apps stores officiels</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Localisation & effacement</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-base">Permissions révisées</span>
                </li>
              </ul>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;